import axiosInstanceAI from "@/api/axiosInstanceAI";
import axiosInstanceNode from "@/api/axiosInstanceNode";

// fetch user accounts
export const fetchAccountsApi = async (
  userId = ""
) => {
  try {
    const response = await axiosInstanceAI.get(
      `/accounts/?user_id=${userId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching OpenAI response:", error);
    throw error;
  }
};

export const createAccountApi = async ({ userId, websiteUrls }) => {
  if (!websiteUrls?.length || websiteUrls.length > 10) {
    throw new Error("Provide between 1 and 10 account links.");
  }

  const response = await axiosInstanceNode.post(
    "/accounts/fetch-contacts-companies-info",
    {
      user_id: userId,
      contact_ids: [],
      company_urls: websiteUrls,
    },
  );
  return response.data;
};

export const createAccountFromContactsApi = async ({ userId, contactIds }) => {
  if (!userId || !Array.isArray(contactIds) || contactIds.length === 0) {
    throw new Error("At least one contact is required to create an account.");
  }

  const response = await axiosInstanceNode.post(
    "/accounts/fetch-contacts-companies-info",
    {
      user_id: userId,
      contact_ids: contactIds,
      company_urls: [],
    },
  );
  return response.data;
};

export const fetchCustomAccountQueriesApi = async () => {
  const response = await axiosInstanceNode.get("/accounts/custom-queries");
  return response.data?.data || [];
};

export const createCustomAccountQueryApi = async ({ name, query }) => {
  const response = await axiosInstanceNode.post("/accounts/custom-queries", {
    name,
    query,
  });
  return response.data?.data;
};

export const runCustomAccountQueryApi = async ({ queryId }) => {
  const response = await axiosInstanceNode.post(
    `/accounts/custom-queries/${encodeURIComponent(queryId)}/run`,
  );
  return response.data?.data;
};

export const selectCustomAccountQueryApi = async ({ queryId }) => {
  const response = await axiosInstanceNode.post(
    `/accounts/custom-queries/${encodeURIComponent(queryId)}/select`,
  );
  return response.data?.data;
};

export const fetchAccountResearchJobApi = async ({ jobId }) => {
  const response = await axiosInstanceNode.get(
    `/accounts/jobs/${encodeURIComponent(jobId)}`,
  );
  return response.data;
};

export const updateAccountStatusApi = async ({
  userId,
  companyName,
  status,
}) => {
  const response = await axiosInstanceAI.patch("/update-accounts/", { status }, {
    params: { user_id: userId, company_name: companyName },
  });
  return response.data;
};

// delete user account
export const deleteAccountApi = async ({user_id, company_name}) => {
  try {
    const response = await axiosInstanceAI.delete(
      `/delete-accounts/?user_id=${encodeURIComponent(user_id)}&company_name=${encodeURIComponent(company_name)}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching OpenAI response:", error);
    throw error;
  }
};

// fetch company info 
export const fetchCompanyInfoApi = async ({userId = "", companyName = ""}) => {
 
  try {
    const response = await axiosInstanceAI.get(
      `/company-info/?user_id=${encodeURIComponent(userId)}&company_name=${encodeURIComponent(companyName)}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching OpenAI response:", error);
    throw error;
  }
};

// fetch account dashboard
export const fetchAccountDashboardApi = async ({ companyName = "", timeZone = "UTC" }) => {
  try {
    const encoded = encodeURIComponent(companyName);
    const response = await axiosInstanceNode.get(
      `/accounts/${encoded}/dashboard`,
      { params: { timeZone } },
    );
    return response.data?.data;
  } catch (error) {
    console.error("Error fetching account dashboard:", error);
    throw error;
  }
};

export const fetchFollowUpAlertApi = async ({ companyName }) => {
  const response = await axiosInstanceNode.get(
    `/accounts/${encodeURIComponent(companyName)}/follow-up-alert`,
  );
  return response.data?.data;
};

export const setFollowUpAlertApi = async ({ companyName, enabled }) => {
  const response = await axiosInstanceNode.put(
    `/accounts/${encodeURIComponent(companyName)}/follow-up-alert`,
    { enabled },
  );
  return response.data?.data;
};

export const dismissFollowUpSuggestionApi = async ({ suggestionId }) => {
  const response = await axiosInstanceNode.delete(
    `/accounts/follow-up-suggestions/${encodeURIComponent(suggestionId)}`,
  );
  return response.data;
};

export const fetchAccountDealsApi = async ({ companyIdentifier }) => {
  const response = await axiosInstanceNode.get(
    `/accounts/${encodeURIComponent(companyIdentifier)}/deals`,
  );
  return response.data?.data;
};

export const createAccountDealApi = async ({ companyIdentifier, ...deal }) => {
  const response = await axiosInstanceNode.post(
    `/accounts/${encodeURIComponent(companyIdentifier)}/deals`,
    deal,
  );
  return response.data?.data;
};

export const updateAccountDealApi = async ({ dealId, ...deal }) => {
  const response = await axiosInstanceNode.patch(
    `/accounts/deals/${encodeURIComponent(dealId)}`,
    deal,
  );
  return response.data?.data;
};

export const deleteAccountDealApi = async ({ dealId }) => {
  const response = await axiosInstanceNode.delete(
    `/accounts/deals/${encodeURIComponent(dealId)}`,
  );
  return response.data?.data;
};

export const fetchAccountDealNotesApi = async ({ dealId }) => {
  const response = await axiosInstanceNode.get(
    `/accounts/deals/${encodeURIComponent(dealId)}/notes`,
  );
  return response.data?.data;
};

export const createAccountDealNoteApi = async ({ dealId, note }) => {
  const response = await axiosInstanceNode.post(
    `/accounts/deals/${encodeURIComponent(dealId)}/notes`,
    note,
  );
  return response.data?.data;
};

export const updateAccountDealNoteApi = async ({ noteId, note }) => {
  const response = await axiosInstanceNode.put(
    `/accounts/deal-notes/${encodeURIComponent(noteId)}`,
    note,
  );
  return response.data?.data;
};

export const deleteAccountDealNoteApi = async ({ noteId }) => {
  const response = await axiosInstanceNode.delete(
    `/accounts/deal-notes/${encodeURIComponent(noteId)}`,
  );
  return response.data?.data;
};

// get chat answer
export const getChatAnswerApi = async ({
  user_id = "",
  prompt = "",
  company_name = "",
  interactions_context = [],
}) => {
  try {
    const formData = new URLSearchParams();
    formData.append("user_id", user_id);
    formData.append("prompt", prompt);
    if (company_name) {
      formData.append("company_name", company_name);
    }
    if (interactions_context?.length) {
      formData.append(
        "interactions_context",
        JSON.stringify(interactions_context.slice(0, 10))
      );
    }

    const baseURL =
      axiosInstanceAI.defaults.baseURL ||
      process.env.REACT_APP_AI_BASE_URL;

    const token = localStorage.getItem("token");

    const response = await fetch(`${baseURL}/conversation-chat/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData.toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }

    // Streaming response
    return response.body;
  } catch (error) {
    console.error("Error fetching chat answer:", error);
    throw error;
  }
};



// fetch chat
export const fetchChatApi = async ({userId = ""}) => {
  try {
    const response = await axiosInstanceAI.get(`/conversation-chat/?user_id=${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching OpenAI response:", error);
    throw error;
  }
};

// --- Conversation-chat threads (multi-agent backend) ---

export const fetchConversationThreads = async ({ user_id, company_name }) => {
  const response = await axiosInstanceAI.get("/conversation-chat/threads", {
    params: { user_id, ...(company_name && { company_name }) },
  });
  return response.data;
};

export const createConversationThread = async ({
  user_id,
  title,
  company_name,
}) => {
  const response = await axiosInstanceAI.post("/conversation-chat/threads", {
    user_id,
    title,
    ...(company_name && { company_name }),
  });
  return response.data;
};

export const renameConversationThread = async ({
  user_id,
  thread_id,
  title,
}) => {
  const response = await axiosInstanceAI.patch(
    `/conversation-chat/threads/${thread_id}`,
    { user_id, title },
  );
  return response.data;
};

export const deleteConversationThread = async ({ user_id, thread_id }) => {
  await axiosInstanceAI.delete(`/conversation-chat/threads/${thread_id}`, {
    params: { user_id },
  });
};

export const fetchConversationThreadMessages = async ({
  user_id,
  thread_id,
}) => {
  const response = await axiosInstanceAI.get(
    `/conversation-chat/threads/${thread_id}/messages`,
    { params: { user_id } },
  );
  return response.data;
};

export const sendConversationChatMessage = async ({
  user_id,
  thread_id,
  prompt,
  company_name,
  signal,
}) => {
  const baseURL =
    axiosInstanceAI.defaults.baseURL || process.env.REACT_APP_AI_BASE_URL;
  const token = localStorage.getItem("token");
  const response = await fetch(
    `${baseURL}/conversation-chat/threads/${thread_id}/messages`,
    {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        user_id,
        prompt,
        ...(company_name && { company_name }),
      }),
    },
  );
  if (!response.ok) {
    throw new Error((await response.text()) || `HTTP ${response.status}`);
  }
  return response.body;
};

// fetch contacts companies info
export const fetchCompanyInfo = async ({userId, contactIds}) => {
  try {
        const response = await axiosInstanceNode.post(`/accounts/fetch-contacts-companies-info`, {
          user_id: userId,
          contact_ids: contactIds,
          company_urls: [],
        }, {
          headers: { 
            'accept': 'application/json',
          }
        });
        return response.data;
  } catch (error) {
    console.error("Error fetching OpenAI response:", error);
    throw error;
  }
};

// fetch account action plan
export const fetchAccountActionPlanApi = async ({
  userId = "",
  organizationId = "",
  companyName = "",
}) => {
  try {
    const response = await axiosInstanceAI.post(`/account-action-plan/`, {
      user_id: userId,
      organization_id: organizationId,
      company_name: companyName,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching OpenAI response:", error);
    throw error;
  }
};

export const fetchAccountRevenueApi = async ({
  userId = "",
  companyName = "",
  companyDomain = "",
}) => {
  const response = await axiosInstanceAI.post("/account-revenue/", {
    user_id: userId,
    company_name: companyName,
    ...(companyDomain ? { company_domain: companyDomain } : {}),
  });
  return response.data;
};

// update account action plan item
export const updateAccountActionPlanApi = async ({
  actionId,
  userId,
  owner,
  dueDate,
  status,
}) => {
  try {
    const payload = {};
    if (owner !== undefined) payload.owner = owner;
    if (dueDate !== undefined) payload.due_date = dueDate;
    if (status !== undefined) payload.status = status;

    const response = await axiosInstanceAI.patch(
      `account-action-plan/${actionId}/?user_id=${encodeURIComponent(userId)}`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error updating action plan:", error);
    throw new Error(
      error.response?.data?.message || "Failed to update action plan"
    );
  }
};


// whitespacing account action plan
export const whitespacingAccountActionPlanApi = async ({
  userId,
  companyName,
}) => {
  try {
    // A fresh analysis runs three research steps and can take a few minutes.
    const response = await axiosInstanceAI.post(
      `/whitespace-audit/`,
      { user_id: userId, company_name: companyName },
      { timeout: 300_000 }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching OpenAI response:", error);
    throw error;
  }
};

// log won/lost for one whitespace card
export const logWhitespaceOutcomeApi = async ({ userId, auditId, outcome }) => {
  const response = await axiosInstanceAI.post(`/whitespace-audit/outcome/`, {
    user_id: userId,
    audit_id: auditId,
    outcome,
  });
  return response.data;
};

// replace the current-vendor list on one whitespace card
export const updateWhitespaceVendorsApi = async ({ userId, auditId, vendors }) => {
  const response = await axiosInstanceAI.post(`/whitespace-audit/vendors/`, {
    user_id: userId,
    audit_id: auditId,
    vendors,
  });
  return response.data;
};
